import {
  Alert,
  Button,
  Card,
  Collapse,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { ActivityContext } from '@/contexts/ActivityContext';
import DottedTimer from '@/components/utils/DottedTimer';
import ClearIcon from '@mui/icons-material/Clear';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'; //toDo move to theme
import ReplayIcon from '@mui/icons-material/Replay';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CustomDurationDialog from './CustomDurationDialog';
import { AiApi, languageKey } from '@api';
import AssistantSuggestionPopper from '@/components/assistant/AssistantSuggestionPopper';
import AssistantSuggestion from '@/components/assistant/AssistantSuggestion';
import RecurrenceForm from './moreOptions/RecurrenceForm';
import DaySelect from './moreOptions/DaySelect';
import TimeSelect from './moreOptions/TimeSelect';
import StartDateText from './moreOptions/StartDateText';
import EndDateSelect from './moreOptions/EndDateSelect';
import MoreOptionsAccordion from './moreOptions/MoreOptionsAccordion';
import { validateRecurrence } from '@/components/utils/Validate';
import { useAiUsable } from '@/utils/ai';

const colors = {
  grey: '#78828A',
  green: '#1F8900',
  blue: '#387EE8',
  purple: '#7338BE',
  red: '#D1435B',
};

// Intervall som stödjer dagval (samma logik som tidigare SetRecurrence-steget).
const weekDayIntervals = ['WEEKLY', 'BIWEEKLY'];

const DurationForm = ({
  validateForm = true,
  hideRecurrence = false,
  onRecurrenceValidChange,
}: {
  validateForm?: boolean;
  hideRecurrence?: boolean;
  // Rapporterar recurrence-validiteten uppåt (används av edit-flödet, som äger
  // aggregerad setValid). I create-flödet är den odefinierad → no-op.
  onRecurrenceValidChange?: (valid: boolean) => void;
}) => {
  const formHandler = useContext(ActivityFormContext);
  const { activity, setActivity } = useContext(ActivityContext);
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const isLanguageKey = (lang: any): lang is languageKey => {
    return ['sv', 'en', 'de', 'fr', 'es', 'it', 'pl', 'nb', 'da', 'fi'].includes(lang);
  };

  // const [error, setError] = useState<boolean | string>(false); // ToDo error handling for custom duration
  const [customDurationModal, setCustomDurationModal] = useState<boolean>(false);

  // Återkommande är en VALFRI inställning på steg 3, AV som default. Slås på/av
  // via en toggle. Vid återbesök (t.ex. redigering av redan återkommande
  // aktivitet) initieras den PÅ om aktiviteten redan har recurringActivity.
  const [recurrenceEnabled, setRecurrenceEnabled] = useState<boolean>(
    () => activity.recurringActivity != null,
  );

  const toggleRecurrence = (enabled: boolean) => {
    setRecurrenceEnabled(enabled);
    // Av → nolla recurrence så icke-återkommande aktiviteter aldrig skapas som
    // återkommande. På → behåll ev. befintlig; användaren väljer intervall.
    if (!enabled && activity.recurringActivity) {
      setActivity({ ...activity, recurringActivity: null });
    }
  };

  const theme = useTheme();

  // AI-förslaget visas bara när driftsättningen HAR en nyckel och användaren har sagt
  // ja. Utan nyckel svarar anropet 500, och en knapp som aldrig levererar är sämre än
  // ingen knapp alls (se utils/ai.ts).
  const aiEnabled = useAiUsable();
  const [durationSuggestion, setDurationSuggestion] = useState<{
    suggestionText: string;
    duration: number;
  } | null>(null);
  const [durationLoading, setDurationLoading] = useState<boolean>(true);
  const [durationError, setDurationError] = useState<boolean>(false);

  useEffect(() => {
    const fetchDurationSuggestions = async () => {
      setDurationLoading(true);
      try {
        const data = await new AiApi().activityDurationSuggestion(
          isLanguageKey(lang) ? lang : 'sv',
          activity,
        );
        if (data) {
          setDurationSuggestion({
            duration: data.duration,
            suggestionText: data.suggestionText,
          });
          setDurationLoading(false);
        } else {
          setDurationError(true);
        }
      } catch (error) {
        setDurationError(true);
      }
    };
    fetchDurationSuggestions();
  }, []);

  useEffect(() => {
    // Recurrence giltig när: dold (ej tillämplig) ELLER toggeln av ELLER giltig
    // via validateRecurrence. Rapporteras alltid uppåt (edit äger aggregerad
    // setValid); create driver setValid själv nedan.
    const recurrenceValid =
      hideRecurrence ||
      !recurrenceEnabled ||
      validateRecurrence(activity.recurringActivity, t) === null;
    onRecurrenceValidChange?.(recurrenceValid);
    if (!validateForm) return;
    const durationValid = activity.duration !== -1;
    // Giltig när duration är satt OCH (återkommande av ELLER giltig recurrence).
    formHandler.setValid(durationValid && recurrenceValid);
  }, [activity, recurrenceEnabled]);

  const isSelected = (durationId: number | 'CustomTime') => {
    if (durationId === 'CustomTime') return ![-1, 0, 15, 30, 45, 60].includes(activity.duration);
    return activity.duration === durationId;
  };

  const getTimeFormat = () => {
    if (activity.duration > 59) {
      const hours = Math.floor(activity.duration / 60);
      const minutes = activity.duration % 60;
      let hourText;
      if (hours == 1) hourText = t('hour');
      else hourText = t('hours');

      if (minutes == 0) return `${hours} ${hourText}`;

      return `${hours} ${hourText} ${minutes} ${t('minutes')}`;
    }

    return `${activity.duration} ${t('min')}`;
  };

  const durationOptions = [
    { time: 15, label: '15 min', iconColor: colors.green },
    { time: 30, label: '30 min', iconColor: colors.purple },
    { time: 45, label: '45 min', iconColor: colors.blue },
    { time: 60, label: '60 min', iconColor: colors.red },
    { time: 0, label: t('Specify no time'), iconColor: colors.grey },
  ];

  return (
    <>
      <Typography variant="subtitle1" textAlign={'center'}>
        {`${t('How much time will it take')}?`}
      </Typography>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 3 }}
        justifyContent={'center'}
      >
        {durationOptions.map((option) => (
          <Grid
            key={option.time}
            item
            xs={6}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              minWidth: '8em',
            })}
          >
            <Button
              sx={(theme) => ({
                m: 0.5,
                flex: 1,
                maxWidth: '20em',
              })}
              onClick={() => setActivity({ ...activity, duration: option.time })}
            >
              <Card
                sx={{
                  width: '100%',
                  outline: isSelected(option.time) ? `3px solid ${theme.palette.primary.main}` : '',
                  position: 'relative',
                }}
              >
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    color: theme.palette.primary.main,
                    visibility: isSelected(option.time) ? 'visible' : 'hidden',
                  }}
                />
                {option.time > 0 ? (
                  <DottedTimer
                    percentage={(option.time / 60) * 100}
                    color={option.iconColor}
                    style={{ width: '50%', margin: 'auto' }}
                  />
                ) : (
                  <ClearIcon sx={{ width: '50%', height: 'unset', color: 'grey' }} />
                )}

                <Typography variant="subtitle1" textAlign={'center'} textTransform={'none'}>
                  {option.label}
                </Typography>
              </Card>
            </Button>
          </Grid>
        ))}
        <Grid
          item
          xs={6}
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'center',
            minWidth: '8em',
          })}
        >
          <Button
            sx={(theme) => ({
              m: 0.5,
              flex: 1,
              maxWidth: '20em',
            })}
            onClick={() => setCustomDurationModal(true)}
          >
            <Card
              sx={{
                width: '100%',
                outline: isSelected('CustomTime') ? `3px solid ${theme.palette.primary.main}` : '',
                position: 'relative',
              }}
            >
              <CheckCircleIcon
                sx={{
                  position: 'absolute',
                  top: 5,
                  right: 5,
                  color: theme.palette.primary.main,
                  visibility: isSelected('CustomTime') ? 'visible' : 'hidden',
                }}
              />
              <SettingsRoundedIcon sx={{ width: '50%', height: 'unset', color: 'grey' }} />
              <Typography variant="subtitle1" textAlign={'center'} textTransform={'none'}>
                {isSelected('CustomTime') ? getTimeFormat() : t('Set my own time')}
              </Typography>
            </Card>
          </Button>
        </Grid>
        <CustomDurationDialog
          open={customDurationModal}
          onChange={(newVal) => setActivity({ ...activity, duration: newVal })}
          onClose={() => setCustomDurationModal(false)}
        />
      </Grid>
      {!hideRecurrence && (
        <>
          <FormControlLabel
            sx={{ mt: 4, ml: 0 }}
            control={
              <Switch
                id="recurrenceToggle"
                checked={recurrenceEnabled}
                onChange={(e) => toggleRecurrence(e.target.checked)}
              />
            }
            label={
              <Typography component="span" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <ReplayIcon sx={{ mr: 1, transform: 'rotate(200deg) scaleY(-1)' }} />
                {t('Recurrence')}
              </Typography>
            }
          />
          <Collapse in={recurrenceEnabled} unmountOnExit>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              {`${t('The activity will recur')}..`}
            </Typography>
            <RecurrenceForm />
            {activity.recurringActivity && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  {t('Recurrence options')}
                </Typography>
                {weekDayIntervals.includes(activity.recurringActivity.recurringInterval) && (
                  <MoreOptionsAccordion
                    headerIcon={<DateRangeIcon sx={{ mr: 1 }} />}
                    headerText={t('Select days')}
                  >
                    <DaySelect />
                  </MoreOptionsAccordion>
                )}
                <MoreOptionsAccordion
                  headerIcon={<ScheduleIcon sx={{ mr: 1 }} />}
                  headerText={t('Select time')}
                >
                  <TimeSelect />
                </MoreOptionsAccordion>
                <StartDateText />
                {activity.recurringActivity.recurringInterval === 'MONTHLY' &&
                  activity.startDate.getDate() > 27 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t(
                        `Note: If the activity starts on a late date (29th, 30th, or 31st) and the month doesn't have enough days, it will be scheduled on the last day of that month instead.`,
                      )}
                    </Alert>
                  )}
                {activity.recurringActivity.recurringInterval === 'YEARLY' &&
                  activity.startDate.getDate() > 28 &&
                  activity.startDate.getMonth() === 1 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t(
                        'Note: If the activity starts on February 29th, it will be scheduled on February 28th in non-leap years.',
                      )}
                    </Alert>
                  )}
                <EndDateSelect />
              </>
            )}
          </Collapse>
        </>
      )}
      {aiEnabled && (
        <AssistantSuggestionPopper
          loading={durationLoading}
          anchorEl={formHandler.assistantAnchorEl ?? null}
          open={!!durationSuggestion && !!formHandler.assistantAnchorEl}
        >
          <AssistantSuggestion
            title={t('Suggested duration:')}
            onAccept={() => {
              if (durationSuggestion) {
                setActivity({ ...activity, duration: durationSuggestion.duration });
              }
            }}
            loading={durationLoading}
            error={durationError}
          >
            <Typography variant="body2" color={theme.palette.grey[50]}>
              {durationLoading ? t('Loading...') : durationSuggestion?.suggestionText}
            </Typography>
          </AssistantSuggestion>
        </AssistantSuggestionPopper>
      )}
    </>
  );
};

export default DurationForm;
